import { z } from 'zod';

import { apiFetch } from '../lib/apiFetch';
import type { Warehouse, ReorderPoint } from '../types/shared';
export type { Warehouse, ReorderPoint };

export interface WarehouseWithRelations extends Warehouse {
  sourceWarehouseId: string | null;
  reorderPoints: ReorderPoint[];
}

export interface WarehouseCreateInput {
  name: string;
  code?: string;
  description?: string;
}

export interface WarehouseUpdateInput {
  name?: string;
  code?: string | null;
  description?: string | null;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// GET /api/v1/warehouses
// ---------------------------------------------------------------------------
export async function fetchWarehouses(includeInactive = false): Promise<Warehouse[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const response = await apiFetch(`/api/v1/warehouses${query}`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch warehouses: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ warehouses: z.array(warehouseSchema) }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid warehouse list response');
  }

  return parsed.data.warehouses;
}

// ---------------------------------------------------------------------------
// GET /api/v1/warehouses/:id
// ---------------------------------------------------------------------------
export async function fetchWarehouseById(id: string): Promise<WarehouseWithRelations> {
  const response = await apiFetch(`/api/v1/warehouses/${id}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Warehouse not found');
    }
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch warehouse: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ warehouse: warehouseWithRelationsSchema }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid warehouse response');
  }

  return parsed.data.warehouse;
}

// ---------------------------------------------------------------------------
// POST /api/v1/warehouses
// ---------------------------------------------------------------------------
export async function createWarehouse(input: WarehouseCreateInput): Promise<Warehouse> {
  const response = await apiFetch('/api/v1/warehouses', {
    method: 'POST',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to create warehouse: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ warehouse: warehouseSchema }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid warehouse response');
  }

  return parsed.data.warehouse;
}

// ---------------------------------------------------------------------------
// PUT /api/v1/warehouses/:id
// ---------------------------------------------------------------------------
export async function updateWarehouse(id: string, input: WarehouseUpdateInput): Promise<Warehouse> {
  const response = await apiFetch(`/api/v1/warehouses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update warehouse: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ warehouse: warehouseSchema }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid warehouse response');
  }

  return parsed.data.warehouse;
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/warehouses/:id
// ---------------------------------------------------------------------------
export async function deactivateWarehouse(id: string): Promise<void> {
  const response = await apiFetch(`/api/v1/warehouses/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to deactivate warehouse: ${response.status}`);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/warehouses/:id/restock-source
// ---------------------------------------------------------------------------
export async function updateRestockSource(
  id: string,
  sourceWarehouseId: string | null,
): Promise<void> {
  const response = await apiFetch(`/api/v1/warehouses/${id}/restock-source`, {
    method: 'PUT',
    body: JSON.stringify({ sourceWarehouseId }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update restock source: ${response.status}`);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/warehouses/:id/reorder-points
// ---------------------------------------------------------------------------
export async function fetchReorderPoints(warehouseId: string): Promise<ReorderPoint[]> {
  const response = await apiFetch(`/api/v1/warehouses/${warehouseId}/reorder-points`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch reorder points: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ reorderPoints: z.array(reorderPointSchema) }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid reorder points response');
  }

  return parsed.data.reorderPoints;
}

// ---------------------------------------------------------------------------
// POST /api/v1/warehouses/:id/reorder-points
// ---------------------------------------------------------------------------
export interface ReorderPointInput {
  inventoryItemId: string;
  defaultReorderThreshold: number;
  defaultReorderQuantity: number;
  overrideReorderThreshold?: number | null;
  overrideReorderQuantity?: number | null;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Response Validation Schemas
// ---------------------------------------------------------------------------

const warehouseSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  type: z.enum(['hub', 'teacher']).default('teacher'),
  description: z.string().nullable(),
  is_active: z.boolean(),
  sourceWarehouseId: z.string().nullable().default(null),
  created_at: z.string().default(() => new Date().toISOString()),
  updated_at: z.string().default(() => new Date().toISOString()),
});

const warehouseWithRelationsSchema = warehouseSchema.extend({
  reorderPoints: z.array(
    z.object({
      id: z.string(),
      warehouse_id: z.string(),
      inventory_item_id: z.string(),
      default_reorder_threshold: z.number(),
      default_reorder_quantity: z.number(),
      override_reorder_threshold: z.number().nullable(),
      override_reorder_quantity: z.number().nullable(),
      notes: z.string().nullable(),
      created_at: z.string(),
      updated_at: z.string(),
    }),
  ),
});

const reorderPointSchema = z.object({
  id: z.string(),
  warehouse_id: z.string(),
  inventory_item_id: z.string(),
  default_reorder_threshold: z.number(),
  default_reorder_quantity: z.number(),
  override_reorder_threshold: z.number().nullable(),
  override_reorder_quantity: z.number().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export async function createReorderPoint(
  warehouseId: string,
  input: ReorderPointInput,
): Promise<ReorderPoint> {
  const response = await apiFetch(`/api/v1/warehouses/${warehouseId}/reorder-points`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to create reorder point: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ reorderPoint: reorderPointSchema }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid reorder point response');
  }

  return parsed.data.reorderPoint;
}

// ---------------------------------------------------------------------------
// PUT /api/v1/warehouses/:id/reorder-points/:pointId
// ---------------------------------------------------------------------------
export async function updateReorderPoint(
  warehouseId: string,
  pointId: string,
  input: ReorderPointInput,
): Promise<ReorderPoint> {
  const response = await apiFetch(`/api/v1/warehouses/${warehouseId}/reorder-points/${pointId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update reorder point: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  const parsed = z.object({ reorderPoint: reorderPointSchema }).safeParse(data);
  if (!parsed.success) {
    throw new Error('Invalid reorder point response');
  }

  return parsed.data.reorderPoint;
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/warehouses/:id/reorder-points/:pointId
// ---------------------------------------------------------------------------
export async function deleteReorderPoint(warehouseId: string, pointId: string): Promise<void> {
  const response = await apiFetch(`/api/v1/warehouses/${warehouseId}/reorder-points/${pointId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to delete reorder point: ${response.status}`);
  }
}
